import React, { useEffect, useState } from 'react';
import { Box, Typography, CircularProgress, Button, Snackbar, Alert, Card, CardContent, Grid } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import CancelSubscriptionDialog from '../../components/modals/CancelSubscriptionDialog';
import { skipWhenOffline, OfflineNotice, useIsOffline } from '../../offline';
import { useFormatInstant } from '../../hooks/useClinicTimezone';
import { INSTANT_FORMATS } from '../../utils/dateUtils';

interface SubscriptionStatusResponse {
  tier: string;
  status: string;
  trialEndsAt?: string | null;
  subscriptionEndsAt?: string | null;
  subscriptionId?: string | null;
  canManage: boolean;
  isFreeTrial?: boolean;
  trialDaysRemaining?: number | null;
  trialExpired?: boolean;
  paymentGraceDaysRemaining?: number | null;
}

interface UsageResponse {
  aiCallsUsed: number;
  aiCallLimit: number;
  veterinariansCount: number;
  veterinarianLimit: number;
  tier: string;
}

const SubscriptionSettings: React.FC = () => {
  const navigate = useNavigate();
  const isOffline = useIsOffline();
  // Every date on this screen is a Stripe period boundary — an INSTANT, shown
  // in the CLINIC's zone. Full reasoning at `trialEnds` below.
  const formatInstant = useFormatInstant();
  const [data, setData] = useState<SubscriptionStatusResponse | null>(null);
  const [usage, setUsage] = useState<UsageResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState<{ open: boolean; msg: string; type: 'success' | 'error' }>({ open: false, msg: '', type: 'success' });
  const [actionLoading, setActionLoading] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);

  const fetchStatus = async () => {
    try {
      const res = await api.get<SubscriptionStatusResponse>('/subscription/status');
      setData(res.data);
    } catch (err: any) {
      console.error(err);
      setSnackbar({ open: true, msg: err.response?.data?.message || 'Failed to load subscription status', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const fetchUsage = async () => {
    try {
      const res = await api.get<UsageResponse>('/subscription/usage');
      setUsage(res.data);
    } catch (err: any) {
      console.error('Failed to fetch usage:', err);
    }
  };

  useEffect(() => {
    // Everything on this tab is Stripe-backed and online-only — skip the doomed
    // fetches (and their raw error snackbar) offline; re-run on reconnect.
    if (!skipWhenOffline()) {
      setLoading(false);
      return;
    }
    setLoading(true);
    fetchStatus();
    fetchUsage();
    // eslint-disable-next-line
  }, [isOffline]);

  const handleCancelClick = () => {
    setShowCancelDialog(true);
  };

  const handleCancelConfirm = async () => {
    setCancelLoading(true);
    try {
      const response = await api.post('/subscription/cancel');
      setSnackbar({ 
        open: true, 
        msg: `Subscription will be canceled on ${formatInstant(
          response.data.endsAt,
          INSTANT_FORMATS.dateNumeric,
        )}`,
        type: 'success' 
      });
      setShowCancelDialog(false);
      fetchStatus();
    } catch (err: any) {
      setSnackbar({ open: true, msg: err.response?.data?.message || 'Failed to cancel subscription', type: 'error' });
    } finally {
      setCancelLoading(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
        <CircularProgress />
      </Box>
    );
  }

  if (!data) {
    // Offline this explains the empty panel; online (a genuine load failure)
    // it renders nothing, preserving the previous snackbar-only behavior.
    return (
      <OfflineNotice sx={{ mb: 0 }}>
        Subscription and billing-plan management need an internet connection.
      </OfflineNotice>
    );
  }

  const isFreeTrial = data.isFreeTrial === true;
  // Duration-neutral on purpose: trials are not all 30 days (the absorbed
  // grandfather cohort runs to Jan-2027), and the real length is already on the
  // line below as days-remaining + end date. A hardcoded '30-Day Free Trial'
  // read as a contradiction beside "129 days remaining".
  const currentTierLabel = isFreeTrial ? 'Free Trial' : (data.tier?.charAt(0) + data.tier?.slice(1).toLowerCase());
  // Clinic.trialEndsAt / .subscriptionEndsAt are INSTANTs — Stripe's `trial_end`
  // and `current_period_end` epochs x 1000 (DATE-KINDS.md 1.3, "Subscriptions/
  // Stripe"), and the `endsAt` in handleCancelConfirm is the same kind of value
  // (subscription.service.ts:1120 `new Date(periodEnd * 1000)`). A period
  // boundary is a real moment, not a calendar day, so it must NOT be date-parted
  // — and the zone that owns it is the clinic's: a period ending 00:30 UTC is
  // "the 14th" in Denver and "the 15th" in London, and the clinic is the party
  // being billed.
  const trialEnds = data.trialEndsAt
    ? formatInstant(data.trialEndsAt, INSTANT_FORMATS.dateNumeric)
    : null;
  const subEnds = data.subscriptionEndsAt
    ? formatInstant(data.subscriptionEndsAt, INSTANT_FORMATS.dateNumeric)
    : null;


  return (
    <Box display="flex" flexDirection="column" gap={3} maxWidth={800}>
      <OfflineNotice sx={{ mb: 0 }}>
        Subscription and billing-plan management need an internet connection.
      </OfflineNotice>
      <Box>
        <Typography variant="h6" gutterBottom>Current Plan</Typography>
        <Typography variant="body1">Plan: {currentTierLabel}</Typography>
        {isFreeTrial ? (
          <>
            <Typography variant="body1" sx={{ color: 'success.main', fontWeight: 500 }}>
              Status: Active Trial
            </Typography>
            {data.trialDaysRemaining != null && (
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                {data.trialDaysRemaining} {data.trialDaysRemaining === 1 ? 'day' : 'days'} remaining
                {trialEnds && ` (ends ${trialEnds})`}
              </Typography>
            )}
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Full access to all features during your trial period.
            </Typography>
          </>
        ) : (
          <>
            <Typography variant="body1">Status: {data.status}</Typography>
            {trialEnds && !isFreeTrial && <Typography variant="body2">Trial ends: {trialEnds}</Typography>}
            {/* During a card-decline grace the period end is the unpaid period's, so it would read as a renewal. */}
            {data.paymentGraceDaysRemaining != null ? (
              <Typography variant="body2" sx={{ color: 'warning.dark', fontWeight: 500 }}>
                Payment failed: {data.paymentGraceDaysRemaining}{' '}
                {data.paymentGraceDaysRemaining === 1 ? 'day' : 'days'} left to update your card
              </Typography>
            ) : (
              subEnds && <Typography variant="body2">Renews on: {subEnds}</Typography>
            )}
          </>
        )}
      </Box>

      {/* Usage Display */}
      {usage && (
        <Box>
          <Typography variant="h6" gutterBottom>Current Usage</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    AI Calls This Month
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'baseline', mb: 1 }}>
                    <Typography variant="h4" component="span">
                      {usage.aiCallsUsed}
                    </Typography>
                    <Typography variant="h6" component="span" color="text.secondary" sx={{ ml: 0.5 }}>
                      {usage.aiCallLimit === -1 ? '/ Unlimited' : `/ ${usage.aiCallLimit}`}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Active Veterinarians
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'baseline', mb: 1 }}>
                    <Typography variant="h4" component="span">
                      {usage.veterinariansCount}
                    </Typography>
                    <Typography variant="h6" component="span" color="text.secondary" sx={{ ml: 0.5 }}>
                      {usage.veterinarianLimit === -1 ? '/ Unlimited' : `/ ${usage.veterinarianLimit}`}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>
      )}

      {isFreeTrial ? (
        <Box>
          <Button
            variant="contained"
            onClick={() => navigate('/paywall')}
            sx={{
              bgcolor: '#C9772B',
              '&:hover': { bgcolor: '#6B3410' },
              textTransform: 'none',
              fontWeight: 600,
            }}
          >
            Choose a Plan
          </Button>
        </Box>
      ) : data.canManage ? (
        <>
          {/* One paid plan, so there is nothing to switch to: the only
              subscription action left for an owner is cancelling. */}
          <Box display="flex" gap={2}>
            {data.subscriptionId && (
              <Button variant="outlined" color="error" onClick={handleCancelClick} disabled={actionLoading || isOffline}>
                Cancel Subscription
              </Button>
            )}
          </Box>
        </>
      ) : (
        <Typography variant="body2" color="text.secondary">
          You do not have permission to manage the subscription. Please contact the clinic owner.
        </Typography>
      )}

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert severity={snackbar.type} sx={{ width: '100%' }}>
          {snackbar.msg}
        </Alert>
      </Snackbar>

      <CancelSubscriptionDialog
        open={showCancelDialog}
        onClose={() => setShowCancelDialog(false)}
        onConfirm={handleCancelConfirm}
        endDate={data?.subscriptionEndsAt || null}
        isLoading={cancelLoading}
      />

    </Box>
  );
};

export default SubscriptionSettings;
